import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { FirebaseAppCheck } from '@capacitor-firebase/app-check';
import { signInWithCustomToken, signOut as firebaseSignOut, type User } from 'firebase/auth';
import { firebaseApp, auth } from '../lib/firebase';
import { initializePlatformServices } from '../lib/platform';
import { runNativeAuthCall } from './nativeFirebaseAuthQueue';

type ExchangeNativeTokenPayload = {
    error?: string;
    customToken?: string;
};

/**
 * Formats auth errors for consistent reporting
 */
const formatAuthError = (error: unknown) => {
    if (!error) {
        return { message: 'Unknown error.' };
    }
    if (typeof error === 'string') {
        return { message: error };
    }
    const err = error as { code?: string; message?: string; name?: string };
    const code = err.code ?? err.name;
    const message = err.message ?? 'Unknown error.';
    return { code, message };
};

/**
 * Gets the base URL for Cloud Functions.
 */
export const getFunctionsBaseUrl = () => {
    const projectId = firebaseApp.options.projectId || 'voxnela';
    return `https://us-central1-${projectId}.cloudfunctions.net`;
};

/**
 * Exchanges a Native ID Token for a Firebase Custom Token via Cloud Function.
 */
export const exchangeNativeToken = async (idToken: string) => {
    if (!Capacitor.isNativePlatform()) {
        throw new Error('Native token exchange is only available in the app.');
    }
    await initializePlatformServices();
    const appCheckToken = (await FirebaseAppCheck.getToken({ forceRefresh: false })).token;
    if (!appCheckToken) {
        throw new Error('App Check token unavailable.');
    }
    const url = `${getFunctionsBaseUrl()}/exchangeNativeToken`;
    console.log('[exchangeNativeToken] Starting fetch to:', url);
    try {
        let status = 0;
        let payload: ExchangeNativeTokenPayload = {};
        const nativeResponse = await CapacitorHttp.request({
            url,
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Firebase-AppCheck': appCheckToken,
            },
            data: { idToken },
            connectTimeout: 15000,
            readTimeout: 15000
        });
        status = nativeResponse.status;
        payload = (nativeResponse.data ?? {}) as ExchangeNativeTokenPayload;

        console.log('[exchangeNativeToken] Response status:', status);

        if (status !== 200) {
            const baseMessage = typeof payload?.error === 'string' ? payload.error : 'Token exchange failed.';
            throw new Error(`${baseMessage} (status ${status})`);
        }

        if (!payload?.customToken) {
            throw new Error('Custom token missing from exchange response.');
        }

        return payload.customToken as string;
    } catch (error) {
        console.error('[exchangeNativeToken] Error:', error);
        const { code, message } = formatAuthError(error);
        throw new Error(code ? `${code}: ${message}` : message);
    }
};

/**
 * Utility to wrap a promise with a timeout.
 */
const withTimeout = <T>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const timeout = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    });
    return Promise.race([promise, timeout]).finally(() => {
        if (timeoutId) clearTimeout(timeoutId);
    }) as Promise<T>;
};

/**
 * Synchronizes the native Firebase user to the Web SDK.
 * This is the most robust way to handle auth on Capacitor iOS.
 */
export const syncNativeAuthToWeb = async (nativeUserResult?: { user?: unknown } | null): Promise<User> => {
    if (!Capacitor.isNativePlatform()) {
        throw new Error('syncNativeAuthToWeb called on web.');
    }

    try {
        const currentUser =
            nativeUserResult ?? (await runNativeAuthCall('getCurrentUser', () => FirebaseAuthentication.getCurrentUser()));
        if (!currentUser?.user) {
            throw new Error('No native user session found.');
        }
        console.log('[syncNativeAuthToWeb] Getting native ID token...');
        const tokenResult = await runNativeAuthCall('getIdToken', () =>
            FirebaseAuthentication.getIdToken({ forceRefresh: false })
        );

        if (!tokenResult?.token) {
            // No native user is signed in
            throw new Error('No native user session found.');
        }

        console.log('[syncNativeAuthToWeb] Exchanging native token for custom token...');
        const customToken = await exchangeNativeToken(tokenResult.token);

        console.log('[syncNativeAuthToWeb] Custom token received, signing in to Web SDK...');
        const credential = await withTimeout(
            signInWithCustomToken(auth, customToken),
            15000,
            'signInWithCustomToken'
        );

        console.log('[syncNativeAuthToWeb] Web SDK sync complete for UID:', credential.user.uid);
        return credential.user;
    } catch (error) {
        const { code, message } = formatAuthError(error);
        if (message === 'No native user session found.') {
            console.log('[syncNativeAuthToWeb] No native session, skipping sync.');
        } else {
            const suffix = code ? ` (${code})` : '';
            console.error(`[syncNativeAuthToWeb] Sync failed${suffix}: ${message}`);
        }
        throw new Error(code ? `${code}: ${message}` : message);
    }
};

/**
 * Signs out from both Web and Native SDKs.
 */
export const signOutUser = async () => {
    try {
        await firebaseSignOut(auth);
        if (Capacitor.isNativePlatform()) {
            try {
                await runNativeAuthCall('signOut', () => FirebaseAuthentication.signOut());
            } catch (nativeErr) {
                console.warn('[signOutUser] Native sign-out failed (skipping):', nativeErr);
            }
        }
    } catch (err) {
        console.error('[signOutUser] Sign-out failed:', err);
        throw err;
    }
};
